import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Settings2, UploadCloud } from 'lucide-react';
import { cn } from '@/lib/cn';
import { ApiError } from '@/lib/api';
import { uploadMatterDocument } from '@/lib/api-client';

// Quick upload for hearing mode (P3 Section 3.7). A compact dropzone above the
// response feed so the operator can drop a document mid-hearing; it goes live in
// the next answer once extracted. The full list lives on the matter documents
// page (linked here).

interface DocumentsQuickUploadProps {
  matterId: string | null;
}

export function DocumentsQuickUpload({ matterId }: DocumentsQuickUploadProps) {
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!matterId) {
    return null;
  }

  async function upload(files: FileList | null): Promise<void> {
    if (!files || files.length === 0 || !matterId) {
      return;
    }
    const file = files[0];
    if (!file) {
      return;
    }
    setBusy(true);
    setIsError(false);
    setStatus(null);
    try {
      await uploadMatterDocument(matterId, file);
      setStatus(`${file.name} uploaded — extracting now`);
    } catch (err) {
      setIsError(true);
      setStatus(
        err instanceof ApiError
          ? err.code === 'DUPLICATE'
            ? `${file.name} is already on this matter`
            : err.message
          : 'Upload failed',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        void upload(e.dataTransfer.files);
      }}
      className={cn(
        'rounded-lg border border-dashed px-3 py-2 transition-colors',
        dragOver ? 'border-amber-500 bg-amber-950/20' : 'border-stone-800 bg-stone-900/40',
      )}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 text-sm text-stone-200 hover:text-amber-400 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin text-amber-500" aria-hidden />
          ) : (
            <UploadCloud className="h-4 w-4 text-amber-500" aria-hidden />
          )}
          Drop doc here
        </button>
        <span className="min-w-0 flex-1 truncate text-[11px] text-stone-500">
          {status ?? 'Goes live in next answer once extracted'}
        </span>
        <Link
          to={`/admin/matters/${matterId}/documents`}
          className="inline-flex shrink-0 items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          title="Manage documents"
        >
          <Settings2 className="h-3.5 w-3.5" />
          Manage
        </Link>
      </div>
      {status && isError ? <p className="mt-1 text-[11px] text-red-400">{status}</p> : null}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          void upload(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
